import dayjs from 'dayjs';
import 'dayjs/locale/es';

import type { BlockConceptsViewDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';

const DISCOUNT_APPLIED_DAY = 2;

/** Sum of the discounts still in force; `discounts.total` counts expired ones too. */
export const getActiveDiscountTotal = (
  block: Pick<SubscriptionBlockViewDto, 'discounts'>,
  reference?: dayjs.ConfigType,
): number => {
  const { discounts } = block;
  if (!discounts) return 0;
  // Without the per-store breakdown there is no expiry to check.
  if (discounts.stores.length === 0) return discounts.total;

  const today = dayjs(reference);
  return discounts.stores.reduce(
    (total, store) =>
      !store.expirationDate || dayjs(store.expirationDate).isAfter(today) ? total + store.amount : total,
    0,
  );
};

/** Whether the block carries a discount that the upcoming charge does not reflect yet. */
export const hasPendingDiscount = (
  block: Pick<SubscriptionBlockViewDto, 'discounts'>,
  concepts: BlockConceptsViewDto,
  reference?: dayjs.ConfigType,
): boolean => {
  if (getActiveDiscountTotal(block, reference) <= 0) return false;
  return concepts.groups.length > 0 && concepts.groups.every((group) => !group.hasDiscount);
};

/** Label for the next 2nd of the month, or `null` on the 2nd itself, when the discount lands. */
export const getPendingDiscountDateLabel = (reference?: dayjs.ConfigType): string | null => {
  const today = dayjs(reference);
  if (today.date() === DISCOUNT_APPLIED_DAY) return null;

  const landingMonth = today.date() < DISCOUNT_APPLIED_DAY ? today : today.add(1, 'month');
  return landingMonth.date(DISCOUNT_APPLIED_DAY).locale('es').format('D [de] MMMM');
};
